import { createSHA256 } from 'hash-wasm'
import { apiBaseUrl, apiClient } from './client'
import type { components } from './schema'
import { UploadError, uploadWithProgress, type UploadProgress } from './upload-with-progress'

/**
 * Chunked document upload (spec 2026-10-02-03).
 *
 * A file over CHUNKED_UPLOAD_THRESHOLD goes up as fixed-size chunks, each one a
 * short request retried on its own, so a network blip costs one chunk instead
 * of the whole file. The server assembles the chunks in a job; this helper
 * polls the upload's status until the document exists.
 */

/** Files over this size take the chunked path; smaller ones keep POST /documents. */
export const CHUNKED_UPLOAD_THRESHOLD = 32 * 1024 * 1024

export type UploadPhase = 'uploading' | 'assembling'

/** Progress of an upload: bytes sent, then the `assembling` phase while the job runs. */
export interface DocumentUploadProgress extends UploadProgress {
  phase?: UploadPhase
}

/** The document fields of POST /documents, sent with the finish request. */
export type ChunkedUploadFields = Omit<
  components['schemas']['DocumentUploadFinishRequest'],
  'sha256' | 'size' | 'chunk_count' | 'file_name' | 'mime_type'
>

export interface ChunkedUploadOptions {
  /** Extra headers on every request (X-Import-Uid). */
  headers?: Record<string, string>
  signal?: AbortSignal
  /** How often the status is polled once finished. Default 2 s. */
  pollIntervalMs?: number
  /** Retries per chunk after the first attempt. Default 3. */
  maxRetries?: number
  /** First retry delay, doubled on each retry. Default 1 s. */
  retryDelayMs?: number
  /** Injected in tests. */
  sleep?: (ms: number) => Promise<void>
}

export interface ChunkedUploadResult {
  upload_id: string
  document_uid: string
}

/** A chunked upload whose assembly failed. `code` is the server's error_code. */
export class ChunkedUploadError extends Error {
  code: string
  uploadId: string

  constructor(code: string, uploadId: string) {
    super(code === 'checksum_mismatch' ? 'Checksum mismatch' : `Upload failed: ${code}`)
    this.name = 'ChunkedUploadError'
    this.code = code
    this.uploadId = uploadId
  }
}

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

function uploadsUrl(org: string): string {
  return `${apiBaseUrl}/api/v1/organizations/${encodeURIComponent(org)}/documents/uploads`
}

/** Turns an openapi-fetch error answer into the same UploadError the XHR path throws. */
function toUploadError(response: Response | undefined, body: unknown): UploadError {
  return new UploadError(response?.status ?? 0, body, response?.headers.get('X-Request-ID') ?? undefined)
}

/** A failure worth retrying: the network, or the server/proxy side. */
function isRetryable(err: unknown): boolean {
  return err instanceof UploadError && (err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500)
}

/**
 * Uploads a file in chunks and resolves once the server created the document.
 * Rejects with an UploadError (an HTTP failure) or a ChunkedUploadError (the
 * assembly failed, `checksum_mismatch` meaning the user should start again).
 */
export async function uploadDocumentChunked(
  org: string,
  file: File,
  fields: ChunkedUploadFields = {},
  onProgress?: (progress: DocumentUploadProgress) => void,
  options: ChunkedUploadOptions = {},
): Promise<ChunkedUploadResult> {
  const { headers, signal, pollIntervalMs = 2000, maxRetries = 3, retryDelayMs = 1000, sleep = defaultSleep } = options
  const aborted = () => {
    if (signal?.aborted) throw new UploadError(0, undefined)
  }

  const created = await apiClient.POST('/api/v1/organizations/{orgCode}/documents/uploads', {
    params: { path: { orgCode: org } },
    headers,
    body: { size: file.size },
  })
  if (created.error || !created.data) throw toUploadError(created.response, created.error)
  const { upload_id: uploadId, chunk_size: chunkSize, chunk_count: chunkCount } = created.data

  const hasher = await createSHA256()
  hasher.init()

  for (let index = 0; index < chunkCount; index++) {
    aborted()
    const start = index * chunkSize
    const chunk = file.slice(start, Math.min(start + chunkSize, file.size))
    // Hashed once, as it is read: a retry re-sends the same bytes.
    hasher.update(new Uint8Array(await chunk.arrayBuffer()))

    for (let attempt = 0; ; attempt++) {
      try {
        await uploadWithProgress({
          url: `${uploadsUrl(org)}/${encodeURIComponent(uploadId)}/chunks/${index}`,
          method: 'PUT',
          body: chunk,
          headers: { ...headers, 'Content-Type': 'application/octet-stream' },
          signal,
          onProgress: onProgress
            ? ({ loaded }) => onProgress({ loaded: start + loaded, total: file.size, phase: 'uploading' })
            : undefined,
        })
        break
      } catch (err) {
        if (attempt >= maxRetries || !isRetryable(err) || signal?.aborted) throw err
        await sleep(retryDelayMs * 2 ** attempt)
      }
    }
    onProgress?.({ loaded: Math.min(start + chunkSize, file.size), total: file.size, phase: 'uploading' })
  }

  const finished = await apiClient.POST('/api/v1/organizations/{orgCode}/documents/uploads/{uploadId}/finish', {
    params: { path: { orgCode: org, uploadId } },
    headers,
    body: {
      ...fields,
      sha256: hasher.digest('hex'),
      size: file.size,
      chunk_count: chunkCount,
      file_name: file.name,
      mime_type: file.type || undefined,
    },
  })
  if (finished.error) throw toUploadError(finished.response, finished.error)

  onProgress?.({ loaded: file.size, total: file.size, phase: 'assembling' })

  for (;;) {
    aborted()
    await sleep(pollIntervalMs)
    const status = await apiClient.GET('/api/v1/organizations/{orgCode}/documents/uploads/{uploadId}', {
      params: { path: { orgCode: org, uploadId } },
    })
    if (status.error || !status.data) {
      // A blip while polling is not a failed upload: the job runs regardless.
      if (isRetryable(toUploadError(status.response, status.error))) continue
      throw toUploadError(status.response, status.error)
    }
    if (status.data.status === 'done' && status.data.document_uid) {
      return { upload_id: uploadId, document_uid: status.data.document_uid }
    }
    if (status.data.status === 'failed') {
      throw new ChunkedUploadError(status.data.error_code ?? 'assembly_failed', uploadId)
    }
  }
}

/** Reads the document fields of a POST /documents FormData. */
export function chunkedFieldsFromFormData(data: FormData): ChunkedUploadFields {
  const fields: Record<string, string | boolean> = {}
  const text = (key: string) => {
    const value = data.get(key)
    return typeof value === 'string' && value !== '' ? value : undefined
  }
  for (const key of [
    'name',
    'code',
    'document_type_uid',
    'asset_uid',
    'contract_uid',
    'link_entity_type',
    'link_entity_uid',
    'link_relation_type',
    'original_path',
  ] as const) {
    const value = text(key)
    if (value !== undefined) fields[key] = value
  }
  const extract = text('extract_archive')
  if (extract !== undefined) fields.extract_archive = extract.toLowerCase() !== 'false'
  // The server validates the vocabulary of link_entity_type & co.
  return fields as ChunkedUploadFields
}

/** The file of a POST /documents FormData when it is big enough for the chunked path. */
export function chunkedUploadFile(data: FormData): File | undefined {
  const file = data.get('file')
  return file instanceof File && file.size > CHUNKED_UPLOAD_THRESHOLD ? file : undefined
}
